// src/views/BuildersInfo.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useFindBuilderStore from "../store/useFindBuilderStore.js";
import { ClipLoader } from "react-spinners";
import useAuthStore from "../store/useAuthStore.js";
import SuccessPopup from "../components/common/SuccessPopup";
import LoginModal from "../components/common/LoginModal.jsx";
import SignupModal from "../components/common/SignupModal.jsx";
import { delayedToast, isButtonDisabled } from "../lib/customToast.js";
import { motion, AnimatePresence } from "framer-motion";
import GenericModal from "../components/common/GenericModal.jsx";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  MapPin,
  Phone,
  AtSign,
  Building2,
  Wrench,
  Camera,
  User,
  MessageSquare,
  Send,
  Compass,
  Home,
  Building,
  Construction,
  Infinity,
  ArrowRight,
  BadgeCheck,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  ArrowLeft,
  Share2,
  Heart,
  ShieldCheck,
} from "lucide-react";

// --- Swiper.js Imports for Carousel ---
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ImageModal from "../components/common/ImageModal";
import useProfileStore from "../store/customer/useProfileStore";
import MobileBottomNav from "../components/common/MobileBottomNav";

// --- Fix for default Leaflet marker icon issue ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const DEFAULT_COVER =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80";
const PLACEHOLDER_LOGO = "/assets/placeholder-avatar.png";

// --- Main Component ---
const BuildersInfo = () => {
  const { builderId } = useParams();
  const navigate = useNavigate();

  // Favorites & Navigation States/Refs
  const [isSaved, setIsSaved] = useState(false);
  const enquiryFormRef = useRef(null);
  // Load favorite state from localStorage on mount/when builderId changes
  useEffect(() => {
    try {
      const savedFavs = localStorage.getItem("ducktail_favorites");
      if (savedFavs) {
        const favsArray = JSON.parse(savedFavs);
        setIsSaved(favsArray.includes(Number(builderId)));
      }
    } catch (e) {
      console.error("Error reading favorites from localStorage:", e);
    }
  }, [builderId]);

  const toggleFavoriteStatus = () => {
    try {
      const savedFavs = localStorage.getItem("ducktail_favorites");
      let favsArray = [];
      if (savedFavs) {
        favsArray = JSON.parse(savedFavs);
      }
      const numId = Number(builderId);
      if (favsArray.includes(numId)) {
        favsArray = favsArray.filter((id) => id !== numId);
        setIsSaved(false);
        delayedToast.success("Removed from Saved Builders!");
      } else {
        favsArray.push(numId);
        setIsSaved(true);
        delayedToast.success("Saved to your profile!");
      }
      localStorage.setItem("ducktail_favorites", JSON.stringify(favsArray));
    } catch (e) {
      console.error("Error updating favorites in localStorage:", e);
    }
  };

  const scrollToEnquiry = () => {
    if (enquiryFormRef.current) {
      enquiryFormRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Zustand Stores
  const {
    builder,
    fetchBuilderInfo,
    isLoading: builderLoading,
    fetchMappedDistricts,
    fetchMappedTaluks,
    sendProjectInvite,
    builders,
    fetchBuilders,
    selectedDistrict,
    selectedTaluk,
    setSelectedDistrict,
    setSelectedTaluk,
  } = useFindBuilderStore();

  const { user, userType, token } = useAuthStore();
  const { profile, fetchProfile } = useProfileStore();

  // State for builder's social media data
  const [builderSocialData, setBuilderSocialData] = useState(null);

  // State for other builders from same location
  const [otherBuilders, setOtherBuilders] = useState([]);

  // Local State
  const [formState, setFormState] = useState({
    phoneNumber: "",
    service: "",
    district: "",
    taluk: "",
    postalCode: "",
    projectLocation: "",
    jobDescription: "",
    additionalNotes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isDirectionsModalOpen, setIsDirectionsModalOpen] = useState(false);
  const [selectedBuilderForDirections, setSelectedBuilderForDirections] =
    useState(null);



  // --- Data Fetching Effects ---
  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchData = async () => {
      if (builderId) {
        await fetchBuilderInfo(builderId);

        try {
          const { axiosInstancev1 } = await import("../lib/axiosInstance");
          const response = await axiosInstancev1.get(
            `/user-info/builder/${builderId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.data && response.data.builder) {
            setBuilderSocialData(response.data.builder);
          }
        } catch (error) {
          console.error("Error fetching builder social data:", error);
        }
      }

      fetchMappedDistricts();
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderId]);

  // Fetch other builders from same district and taluk
  useEffect(() => {
    const fetchOtherBuilders = async () => {
      if (builder && builder.district && builder.taluk) {
        // Set the district and taluk from builder's location
        // REMOVED: Do not overwrite global search state with current builder's location
        // setSelectedDistrict(builder.district);
        // setSelectedTaluk(builder.taluk);

        // Fetch builders from same location
        try {
          const { axiosInstancev1 } = await import("../lib/axiosInstance");
          const params = {
            district: builder.district,
            taluk: builder.taluk,
            sortDir: "asc",
            limit: "4",
            page: "1",
          };
          const queryParams = new URLSearchParams(params).toString();
          const response = await axiosInstancev1.get(
            `/user/builders?${queryParams}`
          );
          if (response.data && response.data.builders) {
            // Filter out current builder
            const filtered = response.data.builders.filter(
              (b) => b.builder_id !== parseInt(builderId)
            );
            setOtherBuilders(filtered.slice(0, 4));
          }
        } catch (error) {
          console.error("Error fetching other builders:", error);
        }
      }
    };

    if (builder) {
      fetchOtherBuilders();
    }
  }, [builder, builderId, setSelectedDistrict, setSelectedTaluk]);

  useEffect(() => {
    if (formState.district) {
      fetchMappedTaluks(formState.district);
    } else {
      useFindBuilderStore.setState({ mappedTaluks: [], selectedTaluk: "" });
      setFormState((prev) => ({ ...prev, taluk: "" }));
    }
  }, [formState.district, fetchMappedTaluks]);

  // Prefill phone number from customer profile if logged in as customer
  useEffect(() => {
    if (token && userType === "customer" && !profile && fetchProfile) {
      fetchProfile();
    }
  }, [token, userType, profile, fetchProfile]);

  useEffect(() => {
    if (
      token &&
      userType === "customer" &&
      profile &&
      profile.contact_number &&
      formState.phoneNumber !== profile.contact_number
    ) {
      setFormState((prev) => ({
        ...prev,
        phoneNumber: profile.contact_number,
      }));
    }
  }, [token, userType, profile, formState.phoneNumber]);

  // Auto-fill district and taluk from builder's location data
  useEffect(() => {
    if (builder) {
      const builderDistrict =
        builder.district || builder.builder_primary_district;
      const builderTaluk = builder.taluk || builder.builder_primary_taluk;

      if (builderDistrict && builderTaluk) {
        setFormState((prev) => ({
          ...prev,
          district: builderDistrict,
          taluk: builderTaluk,
          postalCode: builder.postcode || prev.postalCode,
        }));
      }
    }
  }, [builder]);

  // --- Form Handling ---
  const clearEnquiryForm = () => {
    setFormState({
      phoneNumber: "",
      service: "",
      district: "",
      taluk: "",
      postalCode: "",
      projectLocation: "",
      jobDescription: "",
      additionalNotes: "",
    });
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (!token || userType !== "customer") {
      if (token && userType === "builder") {
        delayedToast.error(
          "You are already logged in as a builder. Please logout and login as a customer to send enquiries.",
          {},
          "builder-login-error"
        );
        return;
      }
      setIsLoginModalOpen(true);
      return;
    }

    const {
      phoneNumber,
      service,
      district,
      taluk,
      postalCode,
      projectLocation,
      jobDescription,
    } = formState;

    if (
      !phoneNumber ||
      !service ||
      !district ||
      !taluk ||
      !postalCode ||
      !projectLocation
    ) {
      delayedToast.error(
        "Please fill all required fields in the enquiry form.",
        {},
        "send-enquiry-btn"
      );
      return;
    }

    // Postal code must be 6 digits
    if (!/^\d{6}$/.test(postalCode)) {
      delayedToast.error(
        "Postal code must be a 6-digit number.",
        {},
        "send-enquiry-btn"
      );
      return;
    }

    setIsSubmitting(true);
    const inviteData = {
      builderId,
      customerId: user.customerId,
      projectName: "Enquiry via Builder Profile",
      projectBudget: "0",
      startingDate: new Date().toISOString().split("T")[0],
      deadlineDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
        .toISOString()
        .split("T")[0],
      projectLocation,
      phoneNumber,
      serviceId: service,
      district,
      taluk,
      postalCode,
      customerName: user.name,
      customerEmail: user.email,
      customerDucktailId: user.customerId,
    };

    const result = await sendProjectInvite(inviteData);
    if (result.success) {
      setIsPopupOpen(true);
      clearEnquiryForm();
    } else {
      delayedToast.error(
        result.message || "Failed to send enquiry.",
        {},
        "send-enquiry-btn"
      );
    }
    setIsSubmitting(false);
  };

  const handleGetDirections = (builder) => {
    if (builder.latitude && builder.longitude) {
      setSelectedBuilderForDirections(builder);
      setIsDirectionsModalOpen(true);
    } else {
      delayedToast.error(
        "Location coordinates are not available for this builder.",
        {},
        "location-error"
      );
    }
  };

  const proceedToGoogleMaps = () => {
    if (
      selectedBuilderForDirections &&
      selectedBuilderForDirections.latitude &&
      selectedBuilderForDirections.longitude
    ) {
      const { latitude, longitude } = selectedBuilderForDirections;
      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
      window.open(mapsUrl, "_blank");
      setIsDirectionsModalOpen(false);
      setSelectedBuilderForDirections(null);
    }
  };

  const handleCloseDirectionsModal = () => {
    setIsDirectionsModalOpen(false);
    setSelectedBuilderForDirections(null);
  };

  // --- Render Logic ---
  if (builderLoading && !builder) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <ClipLoader size={50} color={"#3B82F6"} />
      </div>
    );
  }

  if (!builder) {
    return (
      <div className="text-center p-10 text-gray-600 bg-gray-50 min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold">Builder Not Found</h2>
        <p className="mt-2">The requested builder could not be found.</p>
        <button
          onClick={() => navigate("/builderslist")}
          className="mt-6 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          Back to Builders List
        </button>
      </div>
    );
  }

  const isCustomerLoggedIn = token && userType === "customer";
  const isAnyModalOpen =
    isPopupOpen ||
    isLoginModalOpen ||
    isSignupModalOpen ||
    !!lightboxImage ||
    isDirectionsModalOpen;

  return (
    <>
      <SuccessPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        message="Your enquiry has been sent successfully!"
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={() =>
          delayedToast.success(
            "Login successful! You can now send your enquiry.",
            {},
            "login-success"
          )
        }
      />
      <SignupModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        onSuccess={() => {
          delayedToast.success(
            "Account created! You can now send your enquiry.",
            {},
            "signup-success"
          );
        }}
      />

      <AnimatePresence>
        {lightboxImage && (
          <ImageModal
            images={builder.portfolios?.map((item) => ({
              src: item.image_url,
            }))}
            selectedImage={lightboxImage}
            setSelectedImage={setLightboxImage}
            showNavigation={true}
            hideArrowsOnMobile={true}
          />
        )}
      </AnimatePresence>

      <GenericModal
        isOpen={isDirectionsModalOpen}
        onClose={handleCloseDirectionsModal}
        title="Confirm Directions"
        size="max-w-md"
      >
        {selectedBuilderForDirections && (
          <div>
            <p className="text-sm text-gray-700 mb-1">
              You are about to get directions to:
            </p>
            <div className="bg-gray-50 p-3 rounded-md border border-gray-200 text-xs mb-4 space-y-0.5">
              <p className="font-semibold text-gray-800 text-sm">
                {selectedBuilderForDirections.company_name}
              </p>
              <p>{`${selectedBuilderForDirections.village
                ? `${selectedBuilderForDirections.village}, `
                : ""
                }${selectedBuilderForDirections.taluk || "N/A"}, ${selectedBuilderForDirections.district || "N/A"
                }`}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCloseDirectionsModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-xs font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={proceedToGoogleMaps}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
              >
                <Compass className="w-3.5 h-3.5" />
                Proceed to Maps
              </button>
            </div>
          </div>
        )}
      </GenericModal>

      <div className="bg-gray-50 min-h-screen mobile-content-padding md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" style={{ height: "max-content" }}>
          {/* Breadcrumbs and Share Header */}
          <div className="flex justify-between items-center mb-4 px-1">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 transition duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Results
            </button>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: builder.company_name,
                    text: `Check out ${builder.company_name} on Ducktail!`,
                    url: window.location.href,
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  delayedToast.success("Profile link copied to clipboard!");
                }
              }}
              className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 transition duration-200"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          {/* Builder Header Hero Card */}
          <div className="relative bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden mb-6">
            {/* Cover Banner Image */}
            <div className="relative h-48 sm:h-64 md:h-80 lg:h-[320px] w-full overflow-hidden bg-gray-200">
              <img
                src={builder.cover_image || DEFAULT_COVER}
                alt="Cover"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = DEFAULT_COVER;
                }}
              />
              {/* Dark overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10 md:block hidden" />
            </div>

            {/* Desktop View (md and above) - Overlapping layout inside the banner */}
            <div className="absolute bottom-6 left-6 right-6 md:flex hidden items-end justify-between z-10">
              <div className="flex items-end gap-6">
                {/* Logo Card with whitespace padding */}
                <div className="w-44 h-44 rounded-2xl bg-white shadow-xl overflow-hidden border border-gray-100/80 flex-shrink-0">
                  <img
                    src={builder.profile_image || PLACEHOLDER_LOGO}
                    alt="Logo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = PLACEHOLDER_LOGO;
                    }}
                  />
                </div>

                {/* Title and Info */}
                <div className="text-white mb-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h1 className="text-2xl lg:text-3xl font-extrabold tracking-tight text-white drop-shadow-md">
                      {builder.company_name}
                    </h1>
                    {builder.verified === 1 && (
                      <BadgeCheck className="w-7 h-7 text-white fill-blue-500 flex-shrink-0 drop-shadow-md" />
                    )}
                  </div>
                  
                  {/* Qualification / Subtitle */}
                  <p className="text-base lg:text-lg font-medium text-white mb-3 drop-shadow-sm">
                    {builder.qualification ? `${builder.qualification} Civil Engineer` : "Engineering Your Dreams"}
                  </p>

                  <div className="flex flex-col gap-1.5 text-xs lg:text-sm text-white drop-shadow-sm">
                    <span className="flex items-center gap-2 font-medium">
                      <User className="w-4 h-4 text-white" />
                      Builder Name: <span className="text-white font-bold">{builder.engineer_name || "-"}</span>
                    </span>
                    <span className="flex items-center gap-2 font-medium">
                      <MapPin className="w-4 h-4 text-white" />
                      {builder.village || "Location not specified"}, {builder.taluk}, {builder.district}
                    </span>
                    <span className="flex items-center gap-2 font-medium">
                      <span className="font-semibold text-white">Ducktail ID:</span>
                      <span className="text-white font-bold bg-blue-600/50 px-2.5 py-0.5 rounded text-xs">
                        {builder.ducktail_id}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Desktop Action Buttons */}
              <div className="flex items-center gap-3 mb-1 flex-shrink-0">
                <button
                  onClick={scrollToEnquiry}
                  className="flex items-center gap-2 bg-[#0B2545] hover:bg-[#134074] text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-all duration-300 hover:scale-105 border border-white/10"
                >
                  <MessageSquare className="w-5 h-5" />
                  Contact Now
                </button>
              </div>
            </div>

            {/* Mobile/Tablet View (hidden on md and above) - Content stacked below the banner */}
            <div className="md:hidden block p-5">
              <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-center -mt-16 sm:-mt-20 mb-4">
                {/* Logo Card with proper padding */}
                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl bg-white shadow-xl overflow-hidden border-2 border-white flex-shrink-0 z-10">
                  <img
                    src={builder.profile_image || PLACEHOLDER_LOGO}
                    alt="Logo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = PLACEHOLDER_LOGO;
                    }}
                  />
                </div>
                
                <div className="flex-grow pt-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {builder.company_name}
                    </h1>
                    {builder.verified === 1 && (
                      <BadgeCheck className="w-5 h-5 text-blue-500 fill-white flex-shrink-0" />
                    )}
                  </div>
                  
                  <p className="text-sm font-medium text-blue-600 mt-1">
                    {builder.qualification ? `${builder.qualification} Civil Engineer` : "Engineering Your Dreams"}
                  </p>
                </div>
              </div>

              {/* Detailed Info for Mobile */}
              <div className="space-y-2 mb-5 bg-gray-50 p-4 rounded-2xl border border-gray-100 text-sm text-gray-700">
                <div className="flex items-center justify-between py-1 border-b border-gray-200/60">
                  <span className="font-semibold text-gray-500">Builder Name:</span>
                  <span className="font-bold text-gray-900">{builder.engineer_name || "-"}</span>
                </div>
                <div className="flex items-center justify-between py-1 border-b border-gray-200/60">
                  <span className="font-semibold text-gray-500">Ducktail ID:</span>
                  <span className="font-bold text-blue-600">{builder.ducktail_id}</span>
                </div>
                <div className="flex items-start justify-between py-1">
                  <span className="font-semibold text-gray-500 flex-shrink-0 mr-4">Location:</span>
                  <span className="font-medium text-gray-900 text-right">
                    {builder.village || "Location not specified"}, {builder.taluk}, {builder.district}
                  </span>
                </div>
              </div>

              {/* Mobile Action Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={scrollToEnquiry}
                  className="flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#134074] text-white py-3 px-4 rounded-xl font-bold shadow-md transition w-full"
                >
                  <MessageSquare className="w-5 h-5" />
                  Contact Now
                </button>
              </div>
            </div>
          </div>

          {/* Tab Navigation Bar */}
          <div className="bg-white border-y border-gray-200 sticky top-0 z-30 mb-6 shadow-2xs">
            <div className="max-w-7xl mx-auto flex items-center justify-start gap-4 md:gap-8 px-4 overflow-x-auto hide-scrollbar">
              {[
                { id: "overview", label: "Overview", icon: Home },
                { id: "services", label: "Services", icon: Wrench },
                { id: "gallery", label: "Gallery", icon: Camera },
                
              ].map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      let targetId = tab.id;
                      if (tab.id === "gallery") targetId = "projects";
                      if (tab.id === "about") targetId = "company-details";
                      const el = document.getElementById(targetId);
                      if (el) {
                        const offset = 80;
                        const bodyRect = document.body.getBoundingClientRect().top;
                        const elementRect = el.getBoundingClientRect().top;
                        const elementPosition = elementRect - bodyRect;
                        const offsetPosition = elementPosition - offset;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: "smooth"
                        });
                      }
                    }}
                    className="py-4 px-2 text-xs md:text-sm font-bold text-gray-600 hover:text-blue-600 border-b-2 border-transparent hover:border-blue-600 transition duration-200 cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <TabIcon className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content Two-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Left Column (2/3 width) */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Card */}
              {builder.about && (
                <div id="overview" className="bg-white rounded-3xl border border-gray-200 p-6 sm:p-8 shadow-2xs">
                  <div
                    className="text-gray-650 text-sm sm:text-base leading-relaxed prose prose-sm max-w-none mb-0 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_*:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: builder.about }}
                  />

                  {/* 4 Stats Cards Grid inside the About card */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                    {/* Experience */}
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 flex flex-col items-center text-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mb-2">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        {new Date().getFullYear() - (builder.establishment_year || new Date().getFullYear())}+
                      </span>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-500">Years Experience</span>
                    </div>

                    {/* Projects Completed */}
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 flex flex-col items-center text-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mb-2">
                        <Building className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        {builder.completed_projects_count || 0}+
                      </span>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-500">Projects Completed</span>
                    </div>

                    {/* 10 Year Warranty Agreement */}
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 flex flex-col items-center text-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mb-2">
                        <ShieldCheck className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-base sm:text-lg font-bold text-gray-900">10 Years</span>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-500 leading-snug px-1">
                        Warranty Agreement
                      </span>
                    </div>

                    {/* Certified Builders */}
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 flex flex-col items-center text-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mb-2">
                        <BadgeCheck className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        {builder.verified === 1 ? "Verified" : "Certified"}
                      </span>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-500 leading-snug px-1">
                        Certified Builders
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Our Projects Section - Single Row Horizontal Scroll */}
              {builder.portfolios?.length > 0 && (
                <div id="projects" className="bg-white rounded-3xl border border-gray-200 p-6 sm:p-8 shadow-2xs overflow-hidden">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5">Our Projects</h2>
                  <div className="relative overflow-hidden -mx-2">
                    <div className="builder-projects-track pb-2">
                      {[...builder.portfolios, ...builder.portfolios].map((item, idx) => (
                        <div
                          key={`${item.id || idx}-${idx}`}
                          className="bg-white rounded-2xl border border-gray-150 overflow-hidden shadow-xs cursor-pointer hover:shadow-md transition duration-300 group flex-shrink-0 w-56 sm:w-64"
                          onClick={() => setLightboxImage(item.image_url)}
                        >
                          <div className="relative w-full h-44 sm:h-48 overflow-hidden bg-gray-50">
                            <img
                              src={item.image_url || "/assets/placeholder-image.png"}
                              alt={item.description || "Project"}
                              className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                              onError={(e) => {
                                e.target.src = "/assets/placeholder-image.png";
                              }}
                            />
                            <span className="absolute top-2 right-2 bg-green-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-xs">
                              ✓ Done
                            </span>
                          </div>
                          <div className="p-3">
                            <h3 className="font-bold text-gray-800 text-xs sm:text-sm leading-snug group-hover:text-blue-600 transition duration-200">
                              {item.description ? item.description.replace(/<[^>]*>/g, "") : `Project #${(idx % builder.portfolios.length) + 1}`}
                            </h3>
                            <p className="text-[10px] text-gray-500 mt-0.5 font-semibold">
                              {builder.taluk || "Location"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Services We Offer Section - auto-scroll marquee */}
              {builder.services?.length > 0 && (
                <div id="services" className="bg-white rounded-3xl border border-gray-200 p-6 sm:p-8 shadow-2xs overflow-hidden">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5">Services We Offer</h2>
                  <div className="relative overflow-hidden -mx-2">
                    <div className="builder-services-track pb-2">
                      {[...builder.services, ...builder.services].map((svc, idx) => {
                      let ServiceIcon = Wrench;
                      const name = svc.name.toLowerCase();
                      if (name.includes("residential")) ServiceIcon = Home;
                      else if (name.includes("commercial")) ServiceIcon = Building;
                      else if (name.includes("industrial")) ServiceIcon = Building2;
                      else if (name.includes("renovation") || name.includes("remodel")) ServiceIcon = Wrench;
                      else if (name.includes("interior")) ServiceIcon = Construction;
                      else if (name.includes("consult")) ServiceIcon = Compass;
                      
                      return (
                        <div
                          key={`${svc.id}-${idx}`}
                          className="bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-200 rounded-2xl px-4 py-5 flex flex-col items-center justify-start text-center transition-all duration-300 shadow-2xs hover:shadow-sm group flex-shrink-0 w-[148px] sm:w-[168px] min-h-[8.5rem]"
                        >
                          <div className="w-11 h-11 rounded-full bg-white group-hover:bg-blue-100 border border-gray-100 flex items-center justify-center mb-2 transition duration-200 shadow-xs flex-shrink-0">
                            <ServiceIcon className="w-5 h-5 text-blue-600" />
                          </div>
                          <span className="text-[10px] sm:text-xs font-bold text-gray-700 leading-snug break-words w-full">
                            {svc.name}
                          </span>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column Sidebar (1/3 width) */}
            <div className="space-y-6">
              {/* Contact Information Card */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-2xs">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h2>
                <div className="space-y-4 text-sm text-gray-700 mb-6">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Phone Number</p>
                      <a href={`tel:+91${builder.contact_number}`} className="font-bold text-gray-800 hover:text-blue-600 transition">
                        +91 {builder.contact_number}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Email Address</p>
                      <a href={`mailto:${builder.email}`} className="font-bold text-gray-800 hover:text-blue-600 transition break-all">
                        {builder.email}
                      </a>
                    </div>
                  </div>



                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Office Address</p>
                      <p className="font-bold text-gray-800 leading-snug">
                        {builder.address_line1 && `${builder.address_line1}, `}
                        {builder.address_line2 && `${builder.address_line2}, `}
                        {builder.village && `${builder.village}, `}
                        {builder.taluk && `${builder.taluk}, `}
                        {builder.district && `${builder.district}, `}
                        {builder.state || "Tamil Nadu"} - {builder.postcode}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={scrollToEnquiry}
                    className="w-full bg-[#0B2545] hover:bg-[#134074] text-white py-3.5 rounded-xl font-bold transition shadow-md hover:shadow-lg cursor-pointer text-center block text-sm border border-transparent"
                  >
                    Get a Quote
                  </button>
                  {builder.whatsapp_number && (
                    <a
                      href={`https://wa.me/91${builder.whatsapp_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full border border-gray-200 hover:border-gray-300 text-gray-700 bg-white py-3 rounded-xl font-bold transition flex items-center justify-center gap-2 text-sm shadow-2xs hover:shadow-xs"
                    >
                      <MessageSquare className="w-4 h-4 text-green-500 fill-green-500" />
                      Chat on WhatsApp
                    </a>
                  )}
                </div>
              </div>

              {/* Company Details Card */}
              <div id="company-details" className="bg-white rounded-3xl border border-gray-200 p-6 shadow-2xs">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Company Details</h2>
                <div className="divide-y divide-gray-100 text-sm">
                  <div className="flex justify-between py-2.5">
                    <span className="text-gray-500 font-semibold">Company Type</span>
                    <span className="font-bold text-gray-800">Private Limited</span>
                  </div>
                  <div className="flex justify-between py-2.5">
                    <span className="text-gray-500 font-semibold">Established In</span>
                    <span className="font-bold text-gray-800">{builder.establishment_year}</span>
                  </div>
                  <div className="flex justify-between py-2.5">
                    <span className="text-gray-500 font-semibold">GST No.</span>
                    <span className="font-bold text-gray-800">{builder.gst_number || "33ABCDE1234F1Z5"}</span>
                  </div>
                  <div className="flex justify-between py-2.5">
                    <span className="text-gray-500 font-semibold">Service Area</span>
                    <span className="font-bold text-gray-800 capitalize">{builder.taluk || "Local"} & Surrounding</span>
                  </div>
                </div>
              </div>

              {/* Why Choose Us Card */}
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-2xs">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Why Choose Us?</h2>
                <ul className="space-y-3 text-sm text-gray-700">
                  {[
                    "Quality Construction",
                    "Transparent Pricing",
                    "Timely Project Delivery",
                    "Experienced Professionals",
                    "Customer Satisfaction First",
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2.5 font-bold text-gray-800">
                      <BadgeCheck className="w-5 h-5 text-blue-600 flex-shrink-0 fill-white" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>


            </div>
          </div>

          {/* Bottom Call-To-Action Banner */}

          <style>{`
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            @keyframes builder-projects-scroll {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @keyframes builder-services-scroll {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .builder-projects-track {
              display: flex;
              gap: 16px;
              width: max-content;
              animation: builder-projects-scroll 32s linear infinite;
            }
            .builder-services-track {
              display: flex;
              gap: 16px;
              width: max-content;
              animation: builder-services-scroll 36s linear infinite;
            }
            .builder-projects-track:hover,
            .builder-services-track:hover {
              animation-play-state: paused;
            }
          `}</style>

          {/* Enquiry Form + Map Section */}
          <div ref={enquiryFormRef} className="mt-8 bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
            {/* Section Header */}
            <div className="px-8 pt-8 pb-4 border-b border-gray-100">
              <h2 className="text-2xl font-extrabold text-gray-900">Get in Touch</h2>
              <p className="text-sm text-gray-500 mt-1">Reach out to {builder.company_name} for your next project.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left Side: Location Map + Office Info */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-8 flex flex-col gap-5 border-r border-gray-100">
                {/* Map */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-bold text-gray-700">Office Location</span>
                  </div>
                  {builder.latitude && builder.longitude ? (
                    <div className="h-56 rounded-2xl overflow-hidden border border-gray-200 shadow-sm mb-4 relative">
                      <MapContainer
                        center={[builder.latitude, builder.longitude]}
                        zoom={15}
                        scrollWheelZoom={false}
                        style={{
                          height: "100%",
                          width: "100%",
                          zIndex: isAnyModalOpen ? 0 : 1,
                        }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='© OpenStreetMap contributors'
                        />
                        <Marker position={[builder.latitude, builder.longitude]} />
                      </MapContainer>
                    </div>
                  ) : (
                    <div className="h-56 rounded-2xl bg-gray-100 flex flex-col items-center justify-center mb-4 border border-gray-200">
                      <MapPin className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-gray-500 font-semibold text-sm">Map not available</p>
                    </div>
                  )}
                </div>

                {/* Office Info Cards */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3 bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Address</p>
                      <p className="text-xs text-gray-700 font-semibold leading-relaxed mt-0.5">
                        {builder.address_line1 && `${builder.address_line1}, `}
                        {builder.address_line2 && `${builder.address_line2}, `}
                        {builder.village && `${builder.village}, `}
                        {builder.taluk && `${builder.taluk}, `}
                        {builder.district && `${builder.district}, `}
                        {builder.state || "Tamil Nadu"} - {builder.postcode}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs">
                    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Phone</p>
                      <a href={`tel:+91${builder.contact_number}`} className="text-xs text-gray-800 font-bold hover:text-blue-600 transition">
                        +91 {builder.contact_number}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email</p>
                      <a href={`mailto:${builder.email}`} className="text-xs text-gray-800 font-bold hover:text-blue-600 transition break-all">
                        {builder.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side: Enquiry Form */}
              <div className="p-8">
                {isCustomerLoggedIn ? (
                  <form onSubmit={handleEnquirySubmit} className="space-y-4">
                    <div className="mb-2">
                      <h3 className="text-xl font-bold text-gray-900">Send an Enquiry</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Fill in the details below and we'll get back to you shortly.
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formState.phoneNumber}
                        readOnly
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 bg-gray-100 cursor-not-allowed text-gray-500 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 font-medium"
                        placeholder="10-digit mobile number"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Service Needed <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formState.service}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            service: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white font-medium text-sm"
                        required
                      >
                        <option value="" disabled>
                          -- Choose a service --
                        </option>
                        {builder.services?.map((svc) => (
                          <option key={svc.id} value={svc.id}>
                            {svc.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                          District <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formState.district}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (val.length > 0) val = val.charAt(0).toUpperCase() + val.slice(1);
                            setFormState((prev) => ({
                              ...prev,
                              district: val,
                            }))
                          }}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 font-medium text-sm"
                          placeholder="District"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                          Taluk <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formState.taluk}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (val.length > 0) val = val.charAt(0).toUpperCase() + val.slice(1);
                            setFormState((prev) => ({
                              ...prev,
                              taluk: val,
                            }))
                          }}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 font-medium text-sm"
                          placeholder="Taluk"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Project Location <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formState.projectLocation}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (val.length > 0) val = val.charAt(0).toUpperCase() + val.slice(1);
                          setFormState((prev) => ({
                            ...prev,
                            projectLocation: val,
                          }))
                        }}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 font-medium text-sm"
                        placeholder="e.g., Street, Area"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Postal Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formState.postalCode}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            postalCode: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6),
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 font-medium text-sm"
                        placeholder="6-digit postal code"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-[#0B2545] hover:bg-[#134074] text-white font-bold py-3.5 rounded-xl shadow-md hover:shadow-lg transition flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer text-sm"
                      disabled={
                        isSubmitting || isButtonDisabled("send-enquiry-btn")
                      }
                    >
                      {isSubmitting ? (
                        <ClipLoader size={20} color={"#ffffff"} />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                      Send Enquiry
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Start Your Project?</h3>
                    <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                      Login as a customer to send an enquiry directly to {builder.company_name}.
                    </p>
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => {
                          if (token && userType === "builder") {
                            delayedToast.error(
                              "You are already logged in as a builder.",
                              {},
                              "builder-login-btn-error"
                            );
                            return;
                          }
                          setIsLoginModalOpen(true);
                        }}
                        disabled={isButtonDisabled("builder-login-btn-error")}
                        className="w-full bg-[#0B2545] text-white font-bold py-3.5 px-6 rounded-xl shadow-md hover:bg-[#134074] transition disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer text-sm"
                      >
                        Login to Enquire
                      </button>
                      <button
                        onClick={() => {
                          if (token && userType === "builder") {
                            delayedToast.error(
                              "You are already logged in as a builder.",
                              {},
                              "builder-signup-btn-error"
                            );
                            return;
                          }
                          setIsSignupModalOpen(true);
                        }}
                        disabled={isButtonDisabled("builder-signup-btn-error")}
                        className="w-full bg-gray-100 text-gray-800 font-bold py-3.5 px-6 rounded-xl hover:bg-gray-200 transition disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-gray-500 cursor-pointer text-sm border border-gray-200"
                      >
                        Create an Account
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Other Companies Section */}
          {otherBuilders.length > 0 && (
            <div className="mt-12 mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-6">
                Other Companies
              </h2>
              <Swiper
                modules={[Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                loop={otherBuilders.length > 1}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 20,
                  },
                }}
                className="pb-4"
              >
                {otherBuilders.map((otherBuilder) => (
                  <SwiperSlide key={otherBuilder.builder_id}>
                    <div
                      onClick={() =>
                        navigate(`/buildersinfo/${otherBuilder.builder_id}`)
                      }
                      className="rounded-2xl bg-white border border-gray-200 p-4 shadow-2xs hover:shadow-sm cursor-pointer transition transform hover:-translate-y-1 flex flex-col items-center h-full"
                    >
                      {/* BIG Company Logo/Image */}
                      <div className="mb-4 w-full flex justify-center">
                        <div className="w-full aspect-[16/9] rounded-xl overflow-hidden flex items-center justify-center bg-gray-50 border border-gray-150 shadow-2xs">
                          <img
                            src={
                              otherBuilder.profile_image ||
                              "/assets/placeholder-avatar.png"
                            }
                            alt={otherBuilder.company_name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.src = "/assets/placeholder-avatar.png";
                            }}
                          />
                        </div>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 text-center mb-2 line-clamp-2 min-h-[40px]">
                        {otherBuilder.company_name}
                      </h3>
                      {/* District & Taluk */}
                      <div className="w-full flex items-center justify-center gap-1 mb-2 min-h-[32px]">
                        <span className="text-xs text-blue-600 font-bold text-center capitalize">
                          {(otherBuilder.village || otherBuilder.taluk || "-")}
                          {otherBuilder.taluk ? ", " : ""}
                          {otherBuilder.district && (
                            <>
                              {otherBuilder.village || otherBuilder.taluk ? ", " : ""}
                              {otherBuilder.district}
                            </>
                          )}
                        </span>
                        <ArrowRight className="w-4 h-4 text-blue-600 ml-1" />
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
        </div>
        <MobileBottomNav />
      </div>
    </>
  );
};

export default BuildersInfo;